import React, { useEffect, useState } from "react";
import { 
  User, 
  Building2, 
  Save, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle,
  Edit3
} from "lucide-react";
import { authService } from '../../services/authService';
import tokenStorage from '../../services/tokenStorage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function MiCuentaEditForm() {
  // ✅ Estados para los datos
  const [datosOriginales, setDatosOriginales] = useState(null);
  const [datosActuales, setDatosActuales] = useState({
    // Campos no editables
    tipoCuenta: '',
    tipoDocumento: '',
    numeroIdentificacion: '',
    digitoVerificador: '',
    razonSocial: '',
    primerApellido: '',
    segundoApellido: '',
    nombres: '',
    pais: 'Colombia',
    
    // Campos editables
    direccion: '',
    departamento: '',
    ciudad: '',
    codigoPostal: '',
    ciiu: '',
    telefono: '',
    correo: '',
    correoFacturacion: ''
  });
  
  // Estados de UI
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState({});
  const [hayCambios, setHayCambios] = useState(false);
  
  // Datos de catálogos
  const [tiposCuenta] = useState([
    { value: 'natural', label: 'Persona Natural' },
    { value: 'juridica', label: 'Persona Jurídica' }
  ]);
  
  const [tiposDocumento] = useState([
    { value: 'nit', label: 'NIT' },
    { value: 'cc', label: 'Cédula de Ciudadanía' },
    { value: 'ce', label: 'Cédula de Extranjería' },
    { value: 'ti', label: 'Tarjeta de Identidad' },
  ]);
  
  const [departamentos] = useState([
    { code: '05', name: 'Antioquia' },
    { code: '76', name: 'Valle del Cauca' },
    { code: '11', name: 'Bogotá, D.C.' },
    { code: '13', name: 'Bolívar' },
    { code: '68', name: 'Santander' }
  ]);
  
  const [ciudades, setCiudades] = useState([]);
  
  const ciudadesPorDepartamento = {
    '05': [ 
      { code: '05001', name: 'Medellín' }, 
      { code: '05088', name: 'Bello' },
      { code: '05266', name: 'Envigado' },
      { code: '05380', name: 'Itagüí' }
    ],
    '76': [ 
      { code: '76001', name: 'Cali' }, 
      { code: '76834', name: 'Tuluá' }
    ],
    '11': [ { code: '11001', name: 'Bogotá, D.C.' } ],
    '13': [ { code: '13001', name: 'Cartagena' } ],
    '68': [ { code: '68001', name: 'Bucaramanga' } ]
  };

  // ✅ Campos que SÍ se pueden editar
  const camposEditables = [
    'direccion', 'departamento', 'ciudad', 'codigoPostal', 
    'ciiu', 'telefono', 'correo', 'correoFacturacion'
  ];

  // ✅ Actualizar ciudades cuando cambie departamento
  useEffect(() => {
    if (datosActuales.departamento && ciudadesPorDepartamento[datosActuales.departamento]) {
      setCiudades(ciudadesPorDepartamento[datosActuales.departamento]);
    } else {
      setCiudades([]);
    }
  }, [datosActuales.departamento]);

  // ✅ Cargar datos del usuario al montar
  useEffect(() => {
    cargarDatosUsuario();
  }, []);

  // ✅ Detectar cambios solo en campos editables
  useEffect(() => {
    if (!datosOriginales) return;
    
    const cambios = camposEditables.some(campo => 
      datosActuales[campo] !== (datosOriginales[campo] || '')
    );
    setHayCambios(cambios);
  }, [datosActuales, datosOriginales]);

  // ✅ Función para cargar datos del usuario
  const cargarDatosUsuario = async () => {
    try {
      setIsLoading(true);
      
      // Obtener datos del token storage
      const userData = tokenStorage.getUserData();
      if (!userData) {
        toast.error("No se encontraron datos del usuario");
        return;
      }

      // TODO: Aquí deberías hacer una llamada al API para obtener datos completos
      // Por ahora simulamos con datos del token y algunos hardcodeados
      const datosCompletos = {
        // Datos NO editables
        tipoCuenta: 'natural', // o 'juridica' - esto vendría del API
        tipoDocumento: 'cc',
        numeroIdentificacion: userData.identificationNumber || '123456789',
        digitoVerificador: '7',
        razonSocial: userData.name?.includes('SAS') ? userData.name : 'Mi Empresa SAS',
        nombres: userData.name || 'Usuario',
        primerApellido: 'Apellido1', // Esto vendría del API
        segundoApellido: 'Apellido2',
        pais: 'Colombia',
        
        // Datos editables
        direccion: 'Calle 123 No 45-67',
        departamento: '05',
        ciudad: '05001',
        codigoPostal: '050001',
        ciiu: '4711',
        telefono: '3001234567',
        correo: userData.email || 'usuario@empresa.com',
        correoFacturacion: 'facturacion@empresa.com'
      };

      setDatosOriginales(datosCompletos);
      setDatosActuales(datosCompletos);
      
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error("Error al cargar los datos del usuario");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Solo permitir cambios en campos editables
    if (!camposEditables.includes(name)) {
      return;
    }
    
    setDatosActuales(prev => ({ ...prev, [name]: value }));
    
    // Limpiar error de validación
    if (erroresValidacion[name]) {
      setErroresValidacion(prev => {
        const nuevos = { ...prev };
        delete nuevos[name];
        return nuevos;
      });
    }
  };

  // ✅ Validaciones para campos editables
  const validaciones = {
    direccion: (val) => val && val.trim().length >= 5 && !/[#\-"]/.test(val),
    departamento: (val) => !!val,
    ciudad: (val) => !!val,
    codigoPostal: (val) => /^[0-9]{4,6}$/.test(val),
    telefono: (val) => /^[0-9]{7,10}$/.test(val),
    correo: (val) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val),
    correoFacturacion: (val) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val),
    ciiu: (val) => /^[0-9]{4}$/.test(val)
  };

  const mensajesError = {
    direccion: "Dirección debe tener al menos 5 caracteres y no contener #, - o \"",
    departamento: "Debe seleccionar un departamento",
    ciudad: "Debe seleccionar una ciudad", 
    codigoPostal: "Debe tener entre 4 y 6 dígitos",
    telefono: "Debe tener entre 7 y 10 dígitos",
    correo: "Debe ser un correo electrónico válido",
    correoFacturacion: "Debe ser un correo electrónico válido",
    ciiu: "Código CIIU debe tener exactamente 4 dígitos"
  };

  // ✅ Validar solo campos editables
  const validarFormulario = () => {
    const errores = {};
    
    camposEditables.forEach(campo => {
      if (validaciones[campo] && !validaciones[campo](datosActuales[campo])) {
        errores[campo] = mensajesError[campo];
      }
    });
    
    setErroresValidacion(errores);
    return Object.keys(errores).length === 0;
  };

  // ✅ Guardar cambios
  const guardarCambios = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      toast.error("Por favor corrige los errores en el formulario");
      return;
    }

    if (!hayCambios) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setIsSaving(true);

    try {
      // Preparar payload solo con campos editables modificados
      const cambiosEditables = {};
      camposEditables.forEach(campo => {
        if (datosActuales[campo] !== datosOriginales[campo]) {
          cambiosEditables[campo] = datosActuales[campo];
        }
      });

      // TODO: Llamar al endpoint de actualización
      // const response = await authService.updateUserData(cambiosEditables);
      
      // Simulación de guardado exitoso
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success("✅ Datos actualizados correctamente");
      
      // Actualizar datos originales
      setDatosOriginales({ ...datosActuales });
      setHayCambios(false);
      
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error("❌ Error al actualizar los datos");
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ Restaurar cambios
  const restaurarCambios = () => {
    if (!datosOriginales) return;
    
    setDatosActuales({ ...datosOriginales });
    setErroresValidacion({});
    setHayCambios(false);
    toast.info("Cambios restaurados");
  };

  // ✅ Componente para errores
  const ErrorField = ({ campo }) => {
    if (!erroresValidacion[campo]) return null;
    
    return (
      <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
        <AlertCircle className="w-3 h-3" />
        <span>{erroresValidacion[campo]}</span>
      </div>
    );
  };

  // ✅ Función para determinar si un campo es editable
  const esEditable = (campo) => camposEditables.includes(campo);

  // ✅ Función para obtener clases CSS según editabilidad
  const getFieldClasses = (campo) => {
    const baseClasses = "border rounded-lg h-12 px-3 w-full text-base transition-colors";
    
    if (!esEditable(campo)) {
      // Campo no editable - en sombra
      return `${baseClasses} bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed`;
    }
    
    // Campo editable - normal
    const errorClass = erroresValidacion[campo] ? 'border-red-300 bg-red-50' : 'border-gray-300';
    return `${baseClasses} ${errorClass} focus:ring-2 focus:ring-blue-200`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="text-lg">Cargando datos de la cuenta...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        
        {/* ✅ Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 rounded-full p-3 flex items-center justify-center w-16 h-16">
                {datosActuales.tipoCuenta === 'juridica' ? (
                  <Building2 className="h-10 w-10 text-blue-600" />
                ) : (
                  <User className="h-10 w-10 text-blue-600" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Edit3 className="w-6 h-6 text-blue-600" />
                  Mi Cuenta
                </h1>
                <p className="text-gray-600">
                  {datosActuales.tipoCuenta === 'juridica' ? datosActuales.razonSocial : datosActuales.nombres}
                </p>
                <p className="text-sm text-gray-500">
                  {datosActuales.tipoCuenta === 'juridica' ? 'Persona Jurídica' : 'Persona Natural'}
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">Identificación</div>
              <div className="font-mono font-semibold text-lg">
                {datosActuales.numeroIdentificacion}-{datosActuales.digitoVerificador}
              </div>
              {hayCambios && (
                <div className="flex items-center gap-1 mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  <AlertCircle className="w-3 h-3" />
                  Cambios sin guardar
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ✅ Formulario unificado */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-600" />
              <h3 className="text-xl font-semibold text-gray-800">Información de la Cuenta</h3>
            </div>
            
            <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
              Los campos sombreados no se pueden modificar
            </div>
          </div>

          <form className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" onSubmit={guardarCambios}>
            
            {/* Tipo de Cuenta */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Tipo de Cuenta
              </label>
              <select
                name="tipoCuenta"
                value={datosActuales.tipoCuenta}
                onChange={handleChange}
                className={getFieldClasses('tipoCuenta')}
                disabled={!esEditable('tipoCuenta')}
              >
                <option value="">Seleccione...</option>
                {tiposCuenta.map(tc => (
                  <option key={tc.value} value={tc.value}>{tc.label}</option>
                ))}
              </select>
            </div>

            {/* Tipo de Documento */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Tipo de Documento
              </label>
              <select
                name="tipoDocumento"
                value={datosActuales.tipoDocumento}
                onChange={handleChange}
                className={getFieldClasses('tipoDocumento')}
                disabled={!esEditable('tipoDocumento')}
              >
                <option value="">Seleccione...</option>
                {tiposDocumento.map(td => (
                  <option key={td.value} value={td.value}>{td.label}</option>
                ))}
              </select>
            </div>

            {/* Número Identificación */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Número de Identificación
              </label>
              <input
                name="numeroIdentificacion"
                type="text"
                value={datosActuales.numeroIdentificacion}
                onChange={handleChange}
                className={getFieldClasses('numeroIdentificacion')}
                disabled={!esEditable('numeroIdentificacion')}
              />
            </div>

            {/* Dígito Verificador */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Dígito Verificador
              </label>
              <input
                name="digitoVerificador"
                type="text"
                value={datosActuales.digitoVerificador}
                onChange={handleChange}
                className={getFieldClasses('digitoVerificador')}
                disabled={!esEditable('digitoVerificador')}
              />
            </div>

            {/* Razón Social (solo para Jurídica) */}
            {datosActuales.tipoCuenta === "juridica" && (
              <div className="md:col-span-2">
                <label className="block font-semibold mb-2 text-gray-800">
                  Razón Social
                </label>
                <input
                  name="razonSocial"
                  type="text"
                  value={datosActuales.razonSocial}
                  onChange={handleChange}
                  className={getFieldClasses('razonSocial')}
                  disabled={!esEditable('razonSocial')}
                />
              </div>
            )}

            {/* Campos para Persona Natural */}
            {datosActuales.tipoCuenta === "natural" && (
              <>
                <div>
                  <label className="block font-semibold mb-2 text-gray-800">
                    Primer Apellido
                  </label>
                  <input
                    name="primerApellido"
                    type="text"
                    value={datosActuales.primerApellido}
                    onChange={handleChange}
                    className={getFieldClasses('primerApellido')}
                    disabled={!esEditable('primerApellido')}
                  />
                </div>
                
                <div>
                  <label className="block font-semibold mb-2 text-gray-800">
                    Segundo Apellido
                  </label>
                  <input
                    name="segundoApellido"
                    type="text"
                    value={datosActuales.segundoApellido}
                    onChange={handleChange}
                    className={getFieldClasses('segundoApellido')}
                    disabled={!esEditable('segundoApellido')}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block font-semibold mb-2 text-gray-800">
                    Nombres
                  </label>
                  <input
                    name="nombres"
                    type="text"
                    value={datosActuales.nombres}
                    onChange={handleChange}
                    className={getFieldClasses('nombres')}
                    disabled={!esEditable('nombres')}
                  />
                </div>
              </>
            )}

            {/* Dirección */}
            <div className="md:col-span-2">
              <label className="block font-semibold mb-2 text-gray-800">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                name="direccion"
                type="text"
                value={datosActuales.direccion}
                onChange={e => {
                  const value = e.target.value.replace(/[#\-"]/g, '');
                  handleChange({ target: { name: 'direccion', value } });
                }}
                className={getFieldClasses('direccion')}
                placeholder="Calle 123 No 45-67"
                disabled={!esEditable('direccion')}
              />
              <ErrorField campo="direccion" />
            </div>

            {/* País */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                País
              </label>
              <select
                name="pais"
                value={datosActuales.pais}
                className={getFieldClasses('pais')}
                disabled={!esEditable('pais')}
              >
                <option value="Colombia">Colombia</option>
              </select>
            </div>

            {/* Departamento */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Departamento <span className="text-red-500">*</span>
              </label>
              <select
                name="departamento"
                value={datosActuales.departamento}
                onChange={handleChange}
                className={getFieldClasses('departamento')}
                disabled={!esEditable('departamento')}
              >
                <option value="">Seleccione...</option>
                {departamentos.map(dep => (
                  <option key={dep.code} value={dep.code}>{dep.name}</option>
                ))}
              </select>
              <ErrorField campo="departamento" />
            </div>

            {/* Ciudad */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <select
                name="ciudad"
                value={datosActuales.ciudad}
                onChange={handleChange}
                className={getFieldClasses('ciudad')}
                disabled={!esEditable('ciudad') || !datosActuales.departamento}
              >
                <option value="">
                  {datosActuales.departamento ? 'Seleccione...' : 'Primero seleccione departamento'}
                </option>
                {ciudades.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
              <ErrorField campo="ciudad" />
            </div>

            {/* Código Postal */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                name="codigoPostal"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={datosActuales.codigoPostal}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, '');
                  handleChange({ target: { name: 'codigoPostal', value } });
                }}
                className={getFieldClasses('codigoPostal')}
                placeholder="050001"
                disabled={!esEditable('codigoPostal')}
              />
              <ErrorField campo="codigoPostal" />
            </div>

            {/* CIIU */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Actividad Económica (CIIU) <span className="text-red-500">*</span>
              </label>
              <input
                name="ciiu"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={datosActuales.ciiu}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  handleChange({ target: { name: 'ciiu', value } });
                }}
                className={getFieldClasses('ciiu')}
                placeholder="4711"
                disabled={!esEditable('ciiu')}
              />
              <ErrorField campo="ciiu" />
            </div>

            {/* Teléfono */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Teléfono <span className="text-red-500">*</span>
              </label>
              <input
                name="telefono"
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={datosActuales.telefono}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, '');
                  handleChange({ target: { name: 'telefono', value } });
                }}
                className={getFieldClasses('telefono')}
                placeholder="3001234567"
                disabled={!esEditable('telefono')}
              />
              <ErrorField campo="telefono" />
            </div>

            {/* Correo */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Correo Electrónico <span className="text-red-500">*</span>
              </label>
              <input
                name="correo"
                type="email"
                value={datosActuales.correo}
                onChange={handleChange}
                className={getFieldClasses('correo')}
                placeholder="usuario@empresa.com"
                disabled={!esEditable('correo')}
              />
              <ErrorField campo="correo" />
            </div>

            {/* Correo de Facturación */}
            <div>
              <label className="block font-semibold mb-2 text-gray-800">
                Correo de Facturación <span className="text-red-500">*</span>
              </label>
              <input
                name="correoFacturacion"
                type="email"
                value={datosActuales.correoFacturacion}
                onChange={handleChange}
                className={getFieldClasses('correoFacturacion')}
                placeholder="facturacion@empresa.com"
                disabled={!esEditable('correoFacturacion')}
              />
              <ErrorField campo="correoFacturacion" />
            </div>

            {/* Botones de acción */}
            <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={restaurarCambios}
                disabled={!hayCambios || isSaving}
                className={`flex items-center justify-center gap-2 px-6 py-3 border-2 rounded-xl transition-all duration-200 font-semibold ${
                  hayCambios && !isSaving
                    ? 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    : 'border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <RefreshCw className="w-4 h-4" />
                Restaurar Cambios
              </button>
              
              <button
                type="submit"
                disabled={!hayCambios || isSaving}
                className={`flex-1 flex items-center justify-center gap-2 px-8 py-3 rounded-xl shadow font-bold text-base transition-all duration-200 ${
                  hayCambios && !isSaving
                    ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg transform hover:scale-[1.02]'
                    : 'bg-gray-300 cursor-not-allowed text-gray-500'
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando cambios...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        <ToastContainer 
          position="top-right" 
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </div>
  );
}